/**
 * Where a relay's legs fall, worked out in the browser the way the engine
 * works it out: each leg's offset from the start, and the pieces a start
 * turns into when a leg runs over the end of a position (a day on an hour
 * World, an hour on a minute World). The Loom draws with these before
 * anything is asked, so they must equal what the server will `tell`. Pure.
 *
 * Positions follow the calendar in UTC: a day's position is
 * `{parametric: year, coordinates: [month0, day]}`, an hour's adds the hour.
 */

import type { Json } from '../contracts.ts';
import { slotsMask } from './masks.ts';

export interface Position { readonly parametric: number; readonly coordinates: readonly number[] }
export interface Moment extends Position { readonly slot: number }
export interface At { readonly date: string; readonly hour?: number }
export interface LegSpec { readonly hand: string; readonly length: number; readonly gapAfter?: number; readonly after?: readonly number[] }

/** A leg's stretch at one position: what one ask (one token) covers. */
export interface Piece extends Position {
  readonly leg: number;
  readonly hand: string;
  readonly slots: readonly number[];
  readonly mask: bigint;
  readonly at: At;
}


const pad = (n: number, w = 2): string => String(n).padStart(w, '0');

/** `{date, hour?}` → the position the API resolves it to. */
export const positionOf = (at: At): Position => {
  const [y, m, d] = at.date.split('-').map(Number);
  const coordinates = at.hour === undefined ? [m! - 1, d!] : [m! - 1, d!, at.hour];
  return { parametric: y!, coordinates };
};

/** A position → `{date, hour?}`, the form requests prefer. */
export const atOf = (pos: Position): At => {
  const [month = 0, day = 1, hour] = pos.coordinates;
  const date = `${pad(pos.parametric, 4)}-${pad(month + 1)}-${pad(day)}`;
  return hour === undefined ? { date } : { date, hour };
};

const millisOf = (pos: Position): number => {
  const [month = 0, day = 1, hour = 0] = pos.coordinates;
  const d = new Date(0);
  d.setUTCFullYear(pos.parametric, month, day);
  d.setUTCHours(hour, 0, 0, 0);
  return d.getTime();
};

const positionAt = (ms: number, hourly: boolean): Position => {
  const d = new Date(ms);
  const coordinates = hourly ? [d.getUTCMonth(), d.getUTCDate(), d.getUTCHours()] : [d.getUTCMonth(), d.getUTCDate()];
  return { parametric: d.getUTCFullYear(), coordinates };
};

/** The position `n` steps on (a day, or an hour when the coordinates carry one), by the calendar. */
export const step = (pos: Position, n: number): Position => {
  const hourly = pos.coordinates.length >= 3;
  return positionAt(millisOf(pos) + n * (hourly ? 3_600_000 : 86_400_000), hourly);
};

/** How many positions from `a` to `b` (negative when `b` comes first). */
export const distance = (a: Position, b: Position): number => {
  const hourly = a.coordinates.length >= 3;
  return Math.round((millisOf(b) - millisOf(a)) / (hourly ? 3_600_000 : 86_400_000));
};

export const samePosition = (a: Position, b: Position): boolean =>
  a.parametric === b.parametric && a.coordinates.length === b.coordinates.length && a.coordinates.every((c, i) => c === b.coordinates[i]);

/** The moment `n` slots after `m`, carried across positions (`Relations.advance`). */
export const advance = (m: Moment, n: number, bits: number): Moment => {
  const total = m.slot + n;
  const steps = Math.floor(total / bits);
  const slot = ((total % bits) + bits) % bits;
  return { ...step(m, steps), slot };
};

/** The legs a leg comes after: as it says, or the one before it. */
export const followed = (legs: readonly LegSpec[], i: number): readonly number[] => legs[i]?.after ?? (i > 0 ? [i - 1] : []);

/**
 * Slots from the relay's start to each leg's start: the latest end, interval included, among the
 * legs it follows. Only earlier legs count: the server takes no other, and a draft being built
 * (the Loom) may name one it does not yet follow.
 */
export const offsetsOf = (legs: readonly LegSpec[]): number[] =>
  legs.reduce<number[]>((acc, _, i) => {
    const ends = followed(legs, i).filter((p) => p >= 0 && p < i).map((p) => acc[p]! + legs[p]!.length + (legs[p]!.gapAfter ?? 0));
    return [...acc, Math.max(0, ...ends)];
  }, []);

/** Slots from the start to the end of the last leg to finish. */
export const spanOf = (legs: readonly LegSpec[]): number => {
  const offsets = offsetsOf(legs);
  return legs.reduce((most, leg, i) => Math.max(most, offsets[i]! + leg.length), 0);
};

/** Positions a relay started at `slot` runs over: what `relayWindows` needs in `positions` to list it. */
export const positionsSpanned = (legs: readonly LegSpec[], slot: number, bits: number): number =>
  Math.floor((slot + Math.max(1, spanOf(legs)) - 1) / bits) + 1;

/** The "after" threads between legs (tail → head), for drawing. */
export const threadsOf = (legs: readonly LegSpec[]): { from: number; to: number }[] =>
  legs.flatMap((_, i) => followed(legs, i).map((from) => ({ from, to: i })));

/**
 * The asks a start needs: each leg's stretch, split where it crosses a
 * position, in leg order, as the engine's `pieces` makes them.
 */
export const piecesOf = (legs: readonly LegSpec[], start: Moment, bits: number): Piece[] => {
  const offsets = offsetsOf(legs);
  return legs.flatMap((leg, i) => {
    const out: Piece[] = [];
    const first = advance(start, offsets[i]!, bits);
    let place: Position = first;
    let slot = first.slot;
    let remaining = leg.length;
    while (remaining > 0) {
      const take = Math.min(remaining, bits - slot);
      const slots = Array.from({ length: take }, (_, k) => slot + k);
      out.push({
        leg: i, hand: leg.hand, parametric: place.parametric, coordinates: [...place.coordinates],
        slots, mask: slotsMask(slots), at: atOf(place),
      });
      remaining -= take;
      place = step(place, 1);
      slot = 0;
    }
    return out;
  });
};

/** A relay's legs as the API answers them (`layRelay`, `getRelay`), read loosely. */
export const legsOf = (relay: Json | undefined): LegSpec[] => {
  const legs = relay && typeof relay === 'object' && !Array.isArray(relay) ? relay.legs : null;
  if (!Array.isArray(legs)) return [];
  return legs.flatMap((l) => {
    if (!l || typeof l !== 'object' || Array.isArray(l)) return [];
    const hand = typeof l.hand === 'string' ? l.hand : '';
    const length = typeof l.length === 'number' ? l.length : 0;
    const gapAfter = typeof l.gapAfter === 'number' ? l.gapAfter : undefined;
    const after = Array.isArray(l.after) ? l.after.filter((n): n is number => typeof n === 'number') : undefined;
    return [{ hand, length, ...(gapAfter === undefined ? {} : { gapAfter }), ...(after === undefined ? {} : { after }) }];
  });
};
